import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { Component, Input } from '@angular/core';
import { EgiPage, EgiPageConfig, EgiPagesSnapshot } from '@ervo/page-framework';
import { NavigationItemType } from '@shared/enum/navigation-item-type';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';

const searchProperties: (keyof EgiPageConfig)[] = ['name', 'id', 'topic'];

// Drie elkaar uitsluitende filterstanden in één segment-control:
// 'appMatch' — enkel pagina's die compatibel zijn met de app,
// 'all'      — alle pagina's,
// 'notInUse' — enkel pagina's die nog niet in de menustructuur van de app zitten.
export type PageDragListFilterMode = 'appMatch' | 'all' | 'notInUse';

@Component({
    selector: 'egi-page-drag-list[activeApp]',
    templateUrl: './egi-page-drag-list.component.html',
    styleUrls: ['./egi-page-drag-list.component.css'],
    standalone: false
})
export class EgiPageDragListComponent {
  @Input() activeApp: EgiAppEntity;

  constructor(readonly egiPagesSnapshot: EgiPagesSnapshot) {}

  filterMode: PageDragListFilterMode = 'all';
  searchText = '';
  readonly pageType = NavigationItemType.Page;

  // Reference-compare cache: de set met gebruikte pageComponentId's wordt enkel herberekend
  // wanneer de boom (rootNavigationItem) een nieuwe referentie krijgt. De store vervangt de
  // app-entity bij elke wijziging door een nieuwe instantie van de server, dus een nieuwe
  // boom = nieuwe referentie. De cache staat op de component (niet op het model), zodat de indicator
  // niet bij elke change detection de volledige boom moet overlopen.
  private cachedRoot?: NavigationItemEntity;
  private cachedUsedPageIds = new Set<string>();

  get filteredEgiPages(): EgiPage[] {
    return this.egiPagesSnapshot.pages
      .filter(this.matchesFilter)
      .filter(this.oneOfConfiguredPropertiesContainSearchText)
      .filter(this.isNotClickTroughPage);
  }

  get connectedDropLists(): string[] {
    if (this.activeApp.shouldBeSynced) {
      return [];
    }
    return this.activeApp.rootNavigationItem
      .clone()
      .asArray.filter(i => !i.isClickTroughPage)
      .map(i => i.id);
  }

  isInUse = (p: EgiPage): boolean => this.usedPageComponentIds.has(p.config.id);

  private get usedPageComponentIds(): Set<string> {
    const root = this.activeApp?.rootNavigationItem;
    if (!root) {
      return this.cachedUsedPageIds;
    }
    if (root !== this.cachedRoot) {
      this.cachedRoot = root;
      this.cachedUsedPageIds = new Set(
        root.asArray.map(i => i.pageComponentId).filter((id): id is string => !!id)
      );
    }
    return this.cachedUsedPageIds;
  }

  private matchesFilter = (p: EgiPage): boolean => {
    switch (this.filterMode) {
      case 'appMatch':
        return this.activeApp.isCompatibleWith(p);
      case 'notInUse':
        return !this.isInUse(p);
      default:
        return true;
    }
  }

  private oneOfConfiguredPropertiesContainSearchText = (p: EgiPage) => {
    if (!this.searchText) {
      return true;
    }
    return searchProperties.some(k => {
      const property = p.config[k];
      if (typeof property !== 'string') {
        return false;
      }

      return property.toLowerCase().includes(this.searchText.toLowerCase());
    });
  }

  private isNotClickTroughPage = (p: EgiPage) => !p.config.isClickThroughPage;

  get headerText(): string {
    return `Pagina's (${this.egiPagesSnapshot.pages.length})`;
  }
}
